import { z } from 'zod';

// ── Full title (server → client) ──────────────────────────────────────────────
// A title represents an official job title held during a tenure.
// Multiple titles capture promotions / title changes at the same company.

export const TitleSchema = z.object({
  id: z.string().uuid(),
  userId: z.string().uuid(),
  tenureId: z.string().uuid(),
  title: z.string(),
  startDate: z.string().nullable(),   // YYYY-MM-DD
  endDate: z.string().nullable(),     // YYYY-MM-DD; null = current / ongoing
  sortOrder: z.number().int(),        // user-defined display order within a tenure
  createdAt: z.string().datetime(),
  updatedAt: z.string().datetime(),
});

// ── Create request (client → server) ─────────────────────────────────────────

export const CreateTitleSchema = z.object({
  tenureId: z.string().uuid('Must be a valid tenure ID'),
  title: z.string().min(1, 'Title is required').max(200).trim(),
  startDate: z
    .string()
    .regex(/^\d{4}-\d{2}-\d{2}$/, 'Must be YYYY-MM-DD')
    .optional(),
  endDate: z
    .string()
    .regex(/^\d{4}-\d{2}-\d{2}$/, 'Must be YYYY-MM-DD')
    .optional(),
});

// ── Update request — all fields optional ─────────────────────────────────────

export const UpdateTitleSchema = z.object({
  title: z.string().min(1).max(200).trim().optional(),
  startDate: z
    .string()
    .regex(/^\d{4}-\d{2}-\d{2}$/, 'Must be YYYY-MM-DD')
    .nullable()
    .optional(),
  endDate: z
    .string()
    .regex(/^\d{4}-\d{2}-\d{2}$/, 'Must be YYYY-MM-DD')
    .nullable()
    .optional(),
});

// ── Reorder request ───────────────────────────────────────────────────────────

export const ReorderTitleItemSchema = z.object({
  id: z.string().uuid(),
  sortOrder: z.number().int(),
});

export const ReorderTitlesSchema = z.object({
  reorders: z.array(ReorderTitleItemSchema).min(1),
});

// ── Derived types ─────────────────────────────────────────────────────────────

export type Title = z.infer<typeof TitleSchema>;
export type CreateTitle = z.infer<typeof CreateTitleSchema>;
export type UpdateTitle = z.infer<typeof UpdateTitleSchema>;
export type ReorderTitleItem = z.infer<typeof ReorderTitleItemSchema>;
export type ReorderTitles = z.infer<typeof ReorderTitlesSchema>;
